import * as THREE from 'three';

/**
 * Overboard Mixamo clip tester — cycle packed animations on the player.
 * `[` previous · `]` next · `\` replay · `Backspace` exit test mode
 */
export class AnimTester {
  private names: string[] = [];
  private index = 0;
  private active = false;
  private el: HTMLElement;
  private pendingDelta = 0;
  private pendingReplay = false;
  private pendingExit = false;

  constructor() {
    this.el = document.createElement('div');
    this.el.id = 'anim-tester';
    this.el.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:5.5rem',
      'transform:translateX(-50%)',
      'padding:0.55rem 1rem',
      'background:rgba(8,6,4,0.72)',
      'color:#f0e6d4',
      'font:600 13px/1.35 "Segoe UI",system-ui,sans-serif',
      'letter-spacing:0.04em',
      'border:1px solid rgba(232,220,200,0.22)',
      'border-radius:6px',
      'z-index:40',
      'pointer-events:none',
      'display:none',
      'text-align:center',
      'max-width:min(92vw,28rem)',
    ].join(';');
    document.body.appendChild(this.el);

    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      if (e.code === 'BracketRight') {
        this.pendingDelta = 1;
        e.preventDefault();
      } else if (e.code === 'BracketLeft') {
        this.pendingDelta = -1;
        e.preventDefault();
      } else if (e.code === 'Backslash') {
        this.pendingReplay = true;
        e.preventDefault();
      } else if (e.code === 'Backspace' && this.active) {
        this.pendingExit = true;
        e.preventDefault();
      }
    });
  }

  setClips(names: string[]): void {
    this.names = [...names].sort((a, b) => a.localeCompare(b));
    this.index = Math.min(this.index, Math.max(0, this.names.length - 1));
  }

  isActive(): boolean {
    return this.active;
  }

  currentName(): string | null {
    if (!this.active || this.names.length === 0) return null;
    return this.names[this.index] ?? null;
  }

  /** Call once per frame; returns clip to force-play, or null. */
  poll(): { clip: string | null; exit: boolean } {
    let clip: string | null = null;
    let exit = false;

    if (this.pendingExit) {
      this.pendingExit = false;
      this.active = false;
      this.el.style.display = 'none';
      exit = true;
      return { clip: null, exit };
    }

    if (this.pendingDelta !== 0 && this.names.length > 0) {
      if (!this.active) {
        this.active = true;
        this.index = 0;
      } else {
        this.index =
          (this.index + this.pendingDelta + this.names.length) % this.names.length;
      }
      this.pendingDelta = 0;
      clip = this.names[this.index]!;
      this.render();
    } else if (this.pendingReplay && this.active) {
      this.pendingReplay = false;
      clip = this.names[this.index] ?? null;
      this.render();
    } else {
      this.pendingReplay = false;
      this.pendingDelta = 0;
    }

    return { clip, exit };
  }

  private render(): void {
    if (!this.active || this.names.length === 0) {
      this.el.style.display = 'none';
      return;
    }
    const name = this.names[this.index]!;
    this.el.style.display = 'block';
    this.el.innerHTML = `<div style="opacity:.65;font-size:11px;margin-bottom:2px">ANIM TESTER · [ ] cycle · \\ replay · ⌫ exit</div><div>${this.index + 1}/${this.names.length} — <b>${name}</b></div>`;
  }
}

/** Play a named Mixamo clip once (or loop) on a mixer action map. */
export function playNamedClip(
  mixer: THREE.AnimationMixer,
  actions: Map<string, THREE.AnimationAction>,
  clipsByName: Map<string, THREE.AnimationClip>,
  current: THREE.AnimationAction | null,
  clipName: string,
  fade = 0.18,
): THREE.AnimationAction | null {
  let action = actions.get(clipName);
  if (!action) {
    const clip = clipsByName.get(clipName);
    if (!clip) return current;
    action = mixer.clipAction(clip);
    action.enabled = true;
    actions.set(clipName, action);
  }
  action.reset();
  action.setLoop(THREE.LoopRepeat, Infinity);
  action.clampWhenFinished = false;
  action.setEffectiveWeight(1);
  if (current && current !== action) {
    current.fadeOut(fade);
    action.reset().fadeIn(fade).play();
  } else {
    action.reset().fadeIn(0.05).play();
  }
  return action;
}
